import moment from "moment";
import { DateTime } from "luxon";

/**
 * Formats a list of dates into a string with week ranges.
 * For example, if the input is [2021-01-01, 2021-01-02, 2021-01-03, 2021-01-08, 2021-01-09],
 * the output will be "v. 1-2, v. 8-9".
 * @param dates
 */
export function formatWeekRanges(dates: Date[]): string {
  if (dates.length === 0) return "";

  // First, sort dates just in case they are not sorted
  dates.sort((a, b) => a.getTime() - b.getTime());

  // Convert dates to week numbers
  const weeks = dates.map((date) => parseInt(moment(date).format("WW")));

  // Initialize variables to build the range string
  let result = "";
  let rangeStart = weeks[0];
  let previousWeek = weeks[0];

  for (let i = 1; i < weeks.length; i++) {
    if (weeks[i] === previousWeek + 1) {
      previousWeek = weeks[i];
      continue;
    }

    // If current week is not consecutive, close the previous range and start a new one
    if (previousWeek > rangeStart) {
      result += `v. ${rangeStart}-${previousWeek}, `;
    } else {
      result += `v. ${rangeStart}, `;
    }

    rangeStart = weeks[i];
    previousWeek = weeks[i];
  }

  // Add the last range or week number
  if (previousWeek > rangeStart) {
    result += `v. ${rangeStart}-${previousWeek}`;
  } else {
    result += `v. ${rangeStart}`;
  }

  return result;
}

/**
 * Given a list of date ranges, returns a list of unique week start dates.
 * @param dateRanges
 */
export function getUniqueWeekStarts(dateRanges: Array<{ from: Date; to: Date }>): Date[] {
  const weekStarts = new Set<string>();

  function getMonday(date: Date): Date {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
    return new Date(date.setDate(diff));
  }

  for (const range of dateRanges) {
    let currentDate = getMonday(new Date(range.from));
    const endDate = getMonday(new Date(range.to));

    while (currentDate <= endDate) {
      weekStarts.add(currentDate.toISOString().slice(0, 10)); // store dates as strings to ensure uniqueness
      currentDate = new Date(currentDate.setDate(currentDate.getDate() + 7));
    }
  }

  // Convert the strings back to Date objects and sort
  return Array.from(weekStarts)
    .map((dateStr) => new Date(dateStr))
    .sort((a, b) => a.getTime() - b.getTime());
}

export function localToImplicit(date: Date): Date {
  // Parse the input date to a Luxon DateTime object in its original timezone
  const localDate = DateTime.fromJSDate(date);

  // Convert the local date to UTC and adjust the time to represent the same local time in UTC
  const utcDate = localDate.toUTC().plus({ minutes: localDate.offset });

  return utcDate.toJSDate();
}

export function implicitToLocal(date: Date | string): Date {
  // Parse the input date to a Luxon DateTime object in its original timezone
  const utcDate =
    date instanceof Date
      ? DateTime.fromJSDate(date, { zone: "utc" })
      : DateTime.fromISO(date, { zone: "utc" });

  const localDate = DateTime.fromJSDate(new Date());

  // Convert the local date to UTC and adjust the time to represent the same local time in UTC
  return utcDate.minus({ minutes: localDate.offset }).toJSDate();
}

/**
 * Converts the given date to UTC by removing the local timezone offset.
 *
 * This function takes a Date object and adjusts it by subtracting the local timezone offset.
 * This effectively converts the given date to its corresponding UTC time.
 *
 * @param {Date | null} d - The date to be converted to UTC. If the input is not a valid Date object, null is returned.
 * @returns {Date | null} - The converted date in UTC or null if the input is not a valid Date object.
 */
export function removeTimezoneOffset(d: Date | null) {
  if (!(d instanceof Date)) {
    return null;
  }

  const utcTimestamp = d.getTime() - d.getTimezoneOffset() * 60 * 1000;
  return new Date(utcTimestamp);
}
